import type { FloatingEvents, OpenChangeReason } from "@floating-ui/react";
import type { RefObject } from "react";
import { useEffect, useLayoutEffect, useRef } from "react";

/**
 * Target ref for `FloatingFocusManager`'s `returnFocus`. Holds the element the
 * panel came from while it is open, and nothing once an outside press closed it:
 * the user clicked somewhere else, so focus must not jump back to the trigger.
 */
export const useReturnFocus = (
  events: FloatingEvents,
  open: boolean,
  getSource: () => Element | null,
): RefObject<HTMLElement | null> => {
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      return;
    }

    const source = getSource() ?? document.activeElement;
    returnFocusRef.current = source instanceof HTMLElement ? source : null;
  }, [open, getSource]);

  useEffect(() => {
    const handleOpenChange = (data: {
      open: boolean;
      reason?: OpenChangeReason;
    }) => {
      if (!data.open && data.reason === "outside-press") {
        returnFocusRef.current = null;
      }
    };

    events.on("openchange", handleOpenChange);
    return () => {
      events.off("openchange", handleOpenChange);
    };
  }, [events]);

  return returnFocusRef;
};
